import React, {useCallback, useEffect} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {B2BUserScreens} from '@buncha/B2B/navigation/types';
import BottomSheet from '@buncha/components/composites/bottomSheet';
import {Box} from '@buncha/components/core/view';
import CrossIcon from '@buncha/components/core/icon/CrossIcon';
import {H6Text} from '@buncha/components/core/text';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {PButton} from '@buncha/components/core/Button';
import {PTextInput} from '@buncha/components/core/input/TextInput';
import {Touchable} from '@buncha/components/core/touchable';
import {UpdateDeliveryNotesResponse} from '@buncha/B2B/types';
import font from '@buncha/appStyles/style/font';
import {styles} from './styles';
import {useFetchPostData} from '@buncha/config/hooks/axios';
import {useGetLocale} from '@buncha/localization/hooks';

interface Props {
  open: boolean;
  onClose: () => void;
  notes: string;
  orderId: string | undefined;
  updateData: () => Promise<void>;
  showNotesUpdate: boolean | undefined;
}

const MAX_DELIVERY_TEXT_COUNT = 200;

const UpdateDeliveryBottomSheet = (props: Props) => {
  const {open, onClose, notes, orderId, showNotesUpdate} = props;
  const [feedbackString, setFeedbackString] = React.useState<string>(notes);
  const theme = useColorThemeType();
  const muliFont = useFontTheme();
  const [updateDeliveryNotes, data, loading] =
    useFetchPostData<UpdateDeliveryNotesResponse>();
  const {title} =
    useGetLocale(Modules.B2BUserOrder, B2BUserScreens.OrderInfo) ?? {};

  const handleNotesSubmit = useCallback(async () => {
    if (!orderId) return;

    await updateDeliveryNotes(
      `/api/v1/customer/order/${orderId}/update-delivery-notes`,
      {
        body: {
          note: feedbackString,
        },
      },
    );
  }, [feedbackString, orderId, updateDeliveryNotes]);

  const onChangeText = useCallback((text: string) => {
    if (text.length > 200) return;
    setFeedbackString(text);
  }, []);

  useEffect(() => {
    setFeedbackString(notes);
  }, [open, notes]);

  useEffect(() => {
    if (!data) return;
    onClose();
    props.updateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <BottomSheet open={open} onClose={onClose}>
      <Box style={styles.bottomsheet}>
        <Touchable onPress={onClose} style={styles.closeButton}>
          <CrossIcon
            width="24"
            height="24"
            viewBox="2 2 20 20"
            stroke={theme?.background.gray[100]}
          />
        </Touchable>
        <Box>
          <PTextInput
            value={feedbackString}
            numberOfLines={5}
            placeholder=""
            textAlignVertical={'top'}
            multiline
            debounce={false}
            returnKeyType="next"
            style={[
              styles.input,
              // eslint-disable-next-line react-native/no-inline-styles
              {
                color: theme?.text.blue[300],
                fontFamily: muliFont[400].normal,
                ...font.FontSize.fontH5,
                pointerEvents: showNotesUpdate ? undefined : 'none',
              },
            ]}
            onChangeText={onChangeText}
            containerStyle={[
              styles.inputContainer,
              {
                borderColor: theme?.background.gray[100],
              },
            ]}
          />
          <Box style={styles.textCount}>
            <H6Text>{`${feedbackString.length}/${MAX_DELIVERY_TEXT_COUNT}`}</H6Text>
          </Box>
        </Box>
        <If condition={showNotesUpdate}>
          <PButton
            title={title?.updateInstructions}
            style={styles.actionButton}
            titleStyle={{
              fontFamily: muliFont[700].normal,
            }}
            isLoading={loading}
            changeStyleOnLoading
            loadingText={title?.updateInstructions}
            onPress={handleNotesSubmit}
            bgColor={theme?.icon.blue[500]}
            disabledBgColor={theme?.icon.blue[400]}
          />
        </If>
      </Box>
    </BottomSheet>
  );
};

export default React.memo(UpdateDeliveryBottomSheet);
